import { Modal as AntModal } from 'antd'
import { v4 as uuidv4 } from 'uuid'
import produce from 'immer'
import { moduleNameFromId } from 'admin/lib/constants'
import { checkAllValuesAreDifferent } from './connectionValidation'
import type {
  Connection,
  ConnectionInstrument,
  ConnectionFormData,
  ApiTuple
} from './connectionTypes'

interface Api {
  createNewEmailConnection: (
    accountId: string,
    projectId: string,
    sharedMailboxName: string
  ) => Promise<ApiTuple>
  registerConnection: (
    accountId: string,
    projectId: string,
    data: ConnectionFormData
  ) => Promise<ApiTuple>
  updateConnection: (
    accountId: string,
    projectId: string,
    connectionId: string,
    data: ConnectionFormData
  ) => Promise<ApiTuple>
  updateConnectionInstruments: (
    accountId: string,
    projectId: string,
    connectionId: string,
    data: any[]
  ) => Promise<ApiTuple>
}

export function createEmailSubmissionData(
  connectionId: string,
  projectId: string,
  data: ConnectionFormData
): any[] | null {
  if (!Array.isArray(data.emails)) {
    return []
  }

  const emails = data.emails

  // Validate unique email addresses
  if (!checkAllValuesAreDifferent(emails, 'email')) {
    AntModal.error({
      title: 'Error Saving Connection',
      content: (
        <>
          <p>
            Error, Please use a different email address for each connection.
          </p>
          {emails.map((email, index) => (
            <p key={index}>
              {`Module: ${moduleNameFromId(email.moduleTagDefinitionId)}`}
              <br />
              {`Email: ${email.email}`}
            </p>
          ))}
        </>
      )
    })
    return null
  }

  // Validate unique email identifiers
  if (!checkAllValuesAreDifferent(emails, 'emailIdentifier')) {
    AntModal.error({
      title: 'Error Saving Connection',
      content: (
        <>
          <p>
            Error, Please use a different Email Identifier for each connection.
          </p>
          {emails.map((email, index) => (
            <p key={index}>
              {`Module: ${moduleNameFromId(email.moduleTagDefinitionId)}`}
              <br />
              {`Email: ${email.emailIdentifier}`}
            </p>
          ))}
        </>
      )
    })
    return null
  }

  // Validate unique modules
  if (!checkAllValuesAreDifferent(emails, 'moduleTagDefinitionId')) {
    AntModal.error({
      title: 'Error Saving Connection',
      content: (
        <>
          <p>Error, Please use a different Module for each connection.</p>
          {emails.map((email, index) => (
            <p key={index}>
              {`Module: ${moduleNameFromId(email.moduleTagDefinitionId)}`}
              <br />
              {`Email: ${email.email}`}
            </p>
          ))}
        </>
      )
    })
    return null
  }

  return data.emails.map(email => ({
    externalId: email.isDeleted
      ? `${email.email}#Deleted#${uuidv4()}`
      : email.email,
    connectionId,
    instrumentId: email.instrumentId,
    projectId,
    isDeleted: email.isDeleted,
    feeds: [
      {
        feedId: email.feedId,
        instrumentId: email.instrumentId,
        moduleTagDefinitionId: email.moduleTagDefinitionId,
        additionalFields: {
          permittedFileTypes: email.permittedFileTypes,
          permittedSendersPattern: email.permittedSendersPattern
        },
        externalId: email.isDeleted
          ? `${email.emailIdentifier}#Deleted#${uuidv4()}`
          : email.emailIdentifier
      }
    ]
  }))
}

export function prepareSubmissionData(
  draftItem: ConnectionFormData,
  data: ConnectionFormData,
  isEmailForm: boolean
): ConnectionFormData {
  return produce(draftItem, draft => {
    draft.name = data.name
    draft.connPassword = isEmailForm ? uuidv4() : data.connPassword
    draft.connUsername = isEmailForm ? uuidv4() : data.connUsername
    draft.providerDefinitionId = data.providerDefinitionId
    draft.isChanged = data.isChanged ?? false
  })
}

export async function createNewEmailConnection(
  api: Api,
  accountId: string,
  projectId: string,
  data: ConnectionFormData
): Promise<ApiTuple> {
  const sharedMailboxName = data.sharedMailboxName || ''
  const [responseData, status, error] = await api.createNewEmailConnection(
    accountId,
    projectId,
    sharedMailboxName
  )
  return [responseData, status, error]
}

export async function createExistingEmailConnection(
  api: Api,
  accountId: string,
  projectId: string,
  submissionData: ConnectionFormData,
  data: ConnectionFormData
): Promise<ApiTuple> {
  const [responseData, status, error] = await api.registerConnection(
    accountId,
    projectId,
    submissionData
  )

  if (error) {
    return [responseData, status, error]
  }

  const emailSubmissionData = createEmailSubmissionData(
    responseData.connectionId,
    projectId,
    data
  )

  if (!emailSubmissionData) {
    return [responseData, 0, 'Connection Email Error']
  }

  if (emailSubmissionData.length === 0) {
    return [responseData, status, error]
  }

  return await api.updateConnectionInstruments(
    accountId,
    projectId,
    responseData.connectionId,
    emailSubmissionData
  )
}

export function showErrorModal(status: number, error: string | null): void {
  AntModal.error({
    title: 'Error Saving Connection',
    content: (
      <>
        <p>Error, you may wish to try again.</p>
        <p>{`Details: ${status}: ${error}`}</p>
      </>
    )
  })
}

export function assembleDraftItem(
  connection: Connection,
  instruments: ConnectionInstrument[]
): ConnectionFormData {
  return {
    name: connection.name,
    providerDefinitionId: connection.providerDefinitionId,
    connectionId: connection.connectionId,
    connUsername:
      Array.isArray(connection.incomingCredentials) &&
      connection.incomingCredentials.length > 0
        ? connection.incomingCredentials[
            connection.incomingCredentials.length - 1
          ].userName
        : uuidv4(),
    connPassword:
      Array.isArray(connection.incomingCredentials) &&
      connection.incomingCredentials.length > 0
        ? connection.incomingCredentials[
            connection.incomingCredentials.length - 1
          ].password
        : uuidv4(),
    isChanged: false,
    emails: instruments.map(instrument => ({
      email: instrument.externalId,
      isDeleted: false,
      emailIdentifier: instrument.feeds[0].externalId,
      moduleTagDefinitionId: instrument.feeds[0].moduleTagDefinitionId,
      permittedFileTypes:
        instrument.feeds[0].additionalFields.permittedFileTypes,
      permittedSendersPattern:
        instrument.feeds[0].additionalFields.permittedSendersPattern,
      feedId: instrument.feeds[0].feedId,
      instrumentId: instrument.instrumentId
    }))
  }
}
